import I18n from 'i18n-js';
import _mapKeys from 'lodash/mapKeys';
import _sortBy from 'lodash/sortBy';
import { isEmpty } from 'common/utils/data-utils';
import {
  EXCEL_TABLE_VALUE,
  HIDDEN_DATA_COLUMN,
} from 'constants/excel-constants';
import { REVIEW_ORDER_FIELDS } from 'constants/bulk-upload-constants';

// cell: { r: number, c: number }
export const encodeCell = cell => {
  const row = cell.r + 1;
  let col = '';
  let iCol = cell.c + 1;
  for (iCol; iCol > 0; iCol = Math.floor((iCol - 1) / 26)) {
    col = String.fromCharCode((iCol - 1) % 26 + 65) + col;
  }
  return `${col}${row}`;
};

const parseRow = (sheet, fieldNames, row, startCol, endCol) => {
  const result = {};
  for (let iCol = startCol; iCol <= endCol; iCol += 1) {
    const cell = encodeCell({ r: row, c: iCol });
    const cellValue = sheet.getCell(cell).value;
    const fieldName = fieldNames[iCol - startCol];
    const cellReicever = encodeCell({ r: row, c: iCol });
    const cellReiceverValue = sheet.getCell(cellReicever).value;

    switch (fieldName) {
      case I18n.t(EXCEL_TABLE_VALUE.senderName): {
        result.sender = {
          name: !isEmpty(cellValue) ? String(cellValue) : '',
        };
        break;
      }

      case I18n.t(EXCEL_TABLE_VALUE.senderMobileNumber): {
        result.sender = {
          ...result.sender,
          phone: !isEmpty(cellValue) ? String(cellValue) : '',
        };
        break;
      }

      case I18n.t(EXCEL_TABLE_VALUE.senderAddress): {
        result[REVIEW_ORDER_FIELDS.SENDER_ADDRESS.orderAttribute] = !isEmpty(
          cellValue,
        )
          ? cellValue
          : '';
        break;
      }

      case I18n.t(EXCEL_TABLE_VALUE.senderLocationDetails): {
        result.sender = {
          ...result.sender,
          instruction: !isEmpty(cellValue) ? String(cellValue) : '',
        };
        break;
      }

      case I18n.t(EXCEL_TABLE_VALUE.recipientName): {
        result.recipient = {
          name: !isEmpty(cellReiceverValue) ? String(cellReiceverValue) : '',
        };
        break;
      }

      case I18n.t(EXCEL_TABLE_VALUE.recipientMobileNumber): {
        result.recipient = {
          ...result.recipient,
          phone: !isEmpty(cellReiceverValue) ? String(cellReiceverValue) : '',
        };
        break;
      }

      case I18n.t(EXCEL_TABLE_VALUE.recipientAddress): {
        result[REVIEW_ORDER_FIELDS.RECEIVER_ADDRESS.orderAttribute] = !isEmpty(
          cellReiceverValue,
        )
          ? cellReiceverValue
          : '';
        break;
      }

      case I18n.t(EXCEL_TABLE_VALUE.receiverLocationDetails): {
        result.recipient = {
          ...result.recipient,
          instruction: !isEmpty(cellReiceverValue)
            ? String(cellReiceverValue)
            : '',
        };
        break;
      }

      case I18n.t(EXCEL_TABLE_VALUE.additionalID):
      case I18n.t(EXCEL_TABLE_VALUE.deliveryInstruction): {
        result[REVIEW_ORDER_FIELDS.PARTNER_ORDER_ID.orderAttribute] = !isEmpty(
          cellValue,
        )
          ? String(cellValue)
          : '';
        break;
      }

      case I18n.t(EXCEL_TABLE_VALUE.serviceType): {
        result[REVIEW_ORDER_FIELDS.SERVICE_TYPE.orderAttribute] = !isEmpty(
          cellValue,
        )
          ? cellValue
          : '';
        break;
      }

      case I18n.t(EXCEL_TABLE_VALUE.paymentMethods): {
        const value = !isEmpty(cellValue) ? cellValue : '';
        result[REVIEW_ORDER_FIELDS.PAYMENT.orderAttribute] = value.replace(
          /\s?$/,
          '',
        );
        break;
      }

      case I18n.t(EXCEL_TABLE_VALUE.codItem): {
        if (!isEmpty(cellValue)) {
          const numericValue = Number(cellValue);
          // if value is not a valid number, pass it as it is to show user
          result[REVIEW_ORDER_FIELDS.CASH_ON_DELIVERY.orderAttribute] = isNaN(
            numericValue,
          )
            ? cellValue
            : numericValue;
        }
        break;
      }

      case I18n.t(EXCEL_TABLE_VALUE.promo): {
        if (!isEmpty(cellValue)) {
          result[REVIEW_ORDER_FIELDS.PROMO.orderAttribute] = String(cellValue);
        }
        break;
      }

      case I18n.t(EXCEL_TABLE_VALUE.weight): {
        if (!isEmpty(cellValue)) {
          result[REVIEW_ORDER_FIELDS.WEIGHT.orderAttribute] = Number(cellValue);
        }
        break;
      }

      case I18n.t(EXCEL_TABLE_VALUE.parcelCategory): {
        result[REVIEW_ORDER_FIELDS.PARCEL_CATEGORY.orderAttribute] = !isEmpty(
          cellValue,
        )
          ? cellValue
          : '';
        break;
      }

      case I18n.t(EXCEL_TABLE_VALUE.vehicleType): {
        result[REVIEW_ORDER_FIELDS.VEHICLE_TYPE.orderAttribute] = !isEmpty(
          cellValue,
        )
          ? cellValue
          : '';
        break;
      }

      case I18n.t(EXCEL_TABLE_VALUE.batchReference): {
        if (!isEmpty(cellValue)) {
          result[REVIEW_ORDER_FIELDS.BATCH_REF.orderAttribute] = String(
            cellValue,
          );
        }
        break;
      }

      case I18n.t(EXCEL_TABLE_VALUE.sequenceNumber): {
        if (!isEmpty(cellValue)) {
          result[REVIEW_ORDER_FIELDS.SEQUENCE_NUMBER.orderAttribute] = Number(
            cellValue,
          );
        }
        break;
      }

      case I18n.t(EXCEL_TABLE_VALUE.referenceCode): {
        if (!isEmpty(cellValue)) {
          result[REVIEW_ORDER_FIELDS.REFERENCE_CODE.orderAttribute] = String(
            cellValue,
          );
        }
        break;
      }

      case I18n.t(EXCEL_TABLE_VALUE.itemNote): {
        if (!isEmpty(cellValue)) {
          result.itemNote = String(cellValue);
        }
        break;
      }

      default:
    }
  }
  // skip empty rows
  if (
    (isEmpty(result.destination) && isEmpty(result.origin)) ||
    isEmpty(result.paymentMethod) ||
    isEmpty(result.serviceType)
  ) {
    return null;
  }

  return result;
};

const getFieldNameFromSheet = (sheet, row, startCol, endCol) => {
  const result = [];
  for (let iCol = startCol; iCol <= endCol; iCol += 1) {
    const cell = encodeCell({ r: row, c: iCol });
    const cellValue = sheet.getCell(cell).value;
    result.push(!cellValue ? `NULL_${iCol - startCol}` : cellValue);
  }
  return result;
};

const getDataFromSheet = (sheet, fieldNames, startRow, startCol, endCol) => {
  const result = [];
  let iRow = startRow;
  let flagCell = encodeCell({ r: startRow, c: startCol - 1 });
  const MSDRowsData = {};

  while (sheet.getCell(flagCell).value) {
    let parsedRowValue = parseRow(sheet, fieldNames, iRow, startCol, endCol);
    if (isEmpty(parsedRowValue)) break;

    if (!isEmpty(parsedRowValue.batchReference)) {
      const batchRef = parsedRowValue.batchReference;
      if (isEmpty(MSDRowsData[batchRef])) {
        MSDRowsData[batchRef] = [];
      }
      MSDRowsData[batchRef].push({
        ...parsedRowValue,
        uploadRowIndex: MSDRowsData[batchRef].length + 1, // starts with 1
        isMultipleStop: true,
      });
    } else {
      result.push({
        ...parsedRowValue,
        isMultipleStop: false,
      });
    }

    iRow += 1;
    flagCell = encodeCell({ r: iRow, c: startCol - 1 });
    parsedRowValue = null;
  }

  if (!isEmpty(MSDRowsData)) {
    _mapKeys(MSDRowsData, (batchData, batchKey) => {
      // sort same batch by sequence
      const sortedBatchData = _sortBy(batchData, ['sequenceNumber']).map(
        (data, index) => ({
          ...data,
          itineraryIndex: index + 1, // index 0 is for sender data
        }),
      );
      // Combine MSD rows with same batch ref into 1 result
      const newRowResult = {
        // use first row temporarily for necessary info
        // ...sortedBatchData[0],
        batchReference: sortedBatchData[0].batchReference,
        isMultipleStop: true,
        itinerary: sortedBatchData,
      };
      result.push(newRowResult);
    });
  }

  return result;
};

const parseExcel = (
  file,
  {
    sheetName,
    dataArea, // { headerRow: number, dataRow, number, startCol: number, endCol: number }
  },
  callback,
) => {
  const fileReader = new FileReader();
  fileReader.onload = async event => {
    try {
      const ExcelModule = await import('exceljs/dist/exceljs');
      const Excel = ExcelModule.default;

      const arrayBuffer = fileReader.result;
      const workbook = new Excel.Workbook();

      workbook.xlsx.load(arrayBuffer).then(() => {
        const sheet = workbook.getWorksheet(sheetName);
        if (!sheet) return;

        const { headerRow, dataRow, startCol, endCol } = dataArea;
        const senderNameValue = sheet.getCell('B1').value;
        if (senderNameValue !== I18n.t(EXCEL_TABLE_VALUE.senderName)) {
          callback(null, null);
          return;
        }
        const fieldNames = getFieldNameFromSheet(
          sheet,
          headerRow,
          startCol,
          endCol,
        );
        const cityCell = sheet.getCell(HIDDEN_DATA_COLUMN.CITY_ID).value;
        const serviceTypes = sheet.getCell(HIDDEN_DATA_COLUMN.SERVICE_TYPE)
          .value;
        const data = getDataFromSheet(
          sheet,
          fieldNames,
          dataRow,
          startCol,
          endCol,
        );
        callback(cityCell, data, serviceTypes);
      });
    } catch (error) {
      throw error;
    }
  };

  fileReader.readAsArrayBuffer(file);
};

export default parseExcel;
